import React, { useState, useEffect } from 'react';

interface InstantFeedbackPreviewProps {
  onClick?: () => void;
}

const InstantFeedbackPreview: React.FC<InstantFeedbackPreviewProps> = ({ onClick }) => {
  const [stage, setStage] = useState<'question' | 'answering' | 'correct' | 'incorrect' | 'explanation'>('question');
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);

  // Animate through the feedback flow
  useEffect(() => {
    const runAnimation = () => {
      // Reset
      setStage('question');
      setSelectedAnswer(null);
      setShowExplanation(false);

      // Stage 1: Show question
      setTimeout(() => {
        setStage('answering');
        setSelectedAnswer(2); // Wrong answer first
      }, 1500);

      // Stage 2: Show incorrect feedback
      setTimeout(() => {
        setStage('incorrect');
      }, 2500);

      // Stage 3: Show explanation
      setTimeout(() => {
        setShowExplanation(true);
      }, 3500);

      // Stage 4: Try again - correct answer
      setTimeout(() => {
        setShowExplanation(false);
        setStage('answering');
        setSelectedAnswer(1); // Correct answer
      }, 5500);

      // Stage 5: Show correct feedback
      setTimeout(() => {
        setStage('correct');
      }, 6500);

      // Stage 6: Celebration
      setTimeout(() => {
        setStage('explanation');
      }, 7500);
    };

    runAnimation();
    const interval = setInterval(runAnimation, 10000);
    return () => clearInterval(interval);
  }, []);

  const answers = [
    { id: 0, text: 'x = 2', isCorrect: false },
    { id: 1, text: 'x = 5', isCorrect: true },
    { id: 2, text: 'x = 7', isCorrect: false },
    { id: 3, text: 'x = 3', isCorrect: false },
  ];

  const getAnswerStyle = (answer: typeof answers[0]) => {
    if (selectedAnswer !== answer.id) {
      return {
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: '#94a3b8',
      };
    }

    if (stage === 'answering') {
      return {
        background: 'rgba(59, 130, 246, 0.2)',
        border: '1px solid rgba(59, 130, 246, 0.5)',
        color: '#3b82f6',
      };
    }

    if (stage === 'incorrect' || (stage === 'explanation' && !answer.isCorrect)) {
      return {
        background: 'rgba(239, 68, 68, 0.2)',
        border: '1px solid rgba(239, 68, 68, 0.5)',
        color: '#ef4444',
      };
    }

    if (stage === 'correct' || stage === 'explanation') {
      return {
        background: 'rgba(16, 185, 129, 0.2)',
        border: '1px solid rgba(16, 185, 129, 0.5)',
        color: '#10b981',
      };
    }

    return {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      color: '#94a3b8',
    };
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '320px',
        height: '340px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: stage === 'correct' || stage === 'explanation'
            ? 'rgba(16, 185, 129, 0.1)'
            : stage === 'incorrect'
              ? 'rgba(239, 68, 68, 0.1)'
              : 'rgba(59, 130, 246, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Instant Feedback
        </span>
        <div className="flex items-center gap-1">
          {stage === 'correct' && (
            <>
              <span className="text-sm">✨</span>
              <span className="text-[10px] font-bold" style={{ color: '#10b981' }}>
                +25 XP
              </span>
            </>
          )}
          {stage === 'incorrect' && (
            <span className="text-[10px]" style={{ color: '#f59e0b' }}>
              Try again!
            </span>
          )}
        </div>
      </div>

      {/* Question */}
      <div className="p-4">
        <div className="mb-3">
          <div className="text-[10px] font-medium mb-1" style={{ color: '#64748b' }}>
            YEAR 7 MATHS • LINEAR EQUATIONS
          </div>
          <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>
            Solve: 2x + 3 = 13
          </div>
        </div>

        {/* Answer Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {answers.map((answer) => {
            const style = getAnswerStyle(answer);
            return (
              <div
                key={answer.id}
                className="p-3 rounded-lg text-center transition-all duration-300"
                style={style}
              >
                <span className="text-lg font-bold">{answer.text}</span>
                {selectedAnswer === answer.id && stage === 'correct' && answer.isCorrect && (
                  <span className="ml-2">✓</span>
                )}
                {selectedAnswer === answer.id && stage === 'incorrect' && !answer.isCorrect && (
                  <span className="ml-2">✗</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Feedback Section */}
        <div
          className="rounded-lg p-3 transition-all duration-500"
          style={{
            background: stage === 'correct' || stage === 'explanation'
              ? 'rgba(16, 185, 129, 0.1)'
              : stage === 'incorrect'
                ? 'rgba(239, 68, 68, 0.1)'
                : 'rgba(255, 255, 255, 0.02)',
            border: stage === 'correct' || stage === 'explanation'
              ? '1px solid rgba(16, 185, 129, 0.3)'
              : stage === 'incorrect'
                ? '1px solid rgba(239, 68, 68, 0.3)'
                : '1px solid transparent',
            minHeight: '70px',
          }}
        >
          {stage === 'question' && (
            <div className="text-center py-2">
              <span className="text-xs" style={{ color: '#64748b' }}>
                Select an answer...
              </span>
            </div>
          )}

          {stage === 'answering' && (
            <div className="flex items-center justify-center gap-2 py-2">
              <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#3b82f6', borderTopColor: 'transparent' }} />
              <span className="text-xs" style={{ color: '#3b82f6' }}>
                Checking answer...
              </span>
            </div>
          )}

          {stage === 'incorrect' && !showExplanation && (
            <div className="text-center">
              <div className="text-2xl mb-1">😕</div>
              <div className="text-xs font-medium" style={{ color: '#ef4444' }}>
                Not quite right
              </div>
              <div className="text-[10px] mt-1" style={{ color: '#94a3b8' }}>
                Let's look at why...
              </div>
            </div>
          )}

          {showExplanation && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-5 h-5 rounded flex items-center justify-center"
                  style={{ background: 'rgba(139, 92, 246, 0.2)' }}
                >
                  <span className="text-xs">💡</span>
                </div>
                <span className="text-xs font-medium" style={{ color: '#8b5cf6' }}>
                  Hint
                </span>
              </div>
              <p className="text-[11px]" style={{ color: '#94a3b8' }}>
                To isolate x, first subtract 3 from both sides: 2x = 10. Then divide both sides by 2.
              </p>
            </div>
          )}

          {stage === 'correct' && (
            <div className="text-center">
              <div className="text-2xl mb-1">🎉</div>
              <div className="text-xs font-medium" style={{ color: '#10b981' }}>
                Correct! Well done!
              </div>
              <div className="text-[10px] mt-1" style={{ color: '#94a3b8' }}>
                2x + 3 = 13 → x = 5
              </div>
            </div>
          )}

          {stage === 'explanation' && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm">🌟</span>
                <span className="text-xs font-bold" style={{ color: '#10b981' }}>
                  Mastery increased!
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: '75%', background: '#10b981' }}
                  />
                </div>
                <span className="text-[10px] font-bold" style={{ color: '#10b981' }}>75%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-center gap-2">
          <svg className="w-3 h-3" style={{ color: '#10b981' }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-[10px]" style={{ color: '#94a3b8' }}>
            Instant explanations
          </span>
        </div>
        <span className="text-[10px]" style={{ color: '#64748b' }}>
          24/7 help
        </span>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(6, 182, 212, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default InstantFeedbackPreview;
